import { COLUMN_COUNT } from "@/components/charts/FeaturesTable";
import MolecularStructure from "@/components/charts/MolecularStructure";
import {
  SHOW_AI_NOTE,
  SHOW_JSD_LABEL,
  SHOW_MATCH_PERCENTAGE,
} from "@/config/displayPolicy";
import type { FeatureData } from "@/types/spectrum";
import { formatRt } from "@/utils/formatRt";
import { vpTooltip, vpValueText } from "@/utils/vaporPressure";

interface FeatureDetailInlineRowProps {
  feature: FeatureData | null;
}

function getJsdLabel(jsd: number): { label: string; cls: string } {
  if (jsd < 0.2) return { label: "신뢰 높음", cls: "text-green-700" };
  if (jsd < 0.5) return { label: "확인 필요", cls: "text-yellow-700" };
  return { label: "매칭 약함", cls: "text-red-700" };
}

/**
 * FeatureDetailInlineRow — tablet/mobile expand-row 상세 패널 (v2 spec §3, K2).
 * FeaturesTable 행 바로 아래에 삽입되는 인라인 상세 뷰.
 * feature가 null이면 렌더하지 않음.
 */
export default function FeatureDetailInlineRow({
  feature,
}: FeatureDetailInlineRowProps) {
  if (!feature) return null;

  const jsdInfo =
    SHOW_JSD_LABEL && feature.jsd_distance != null
      ? getJsdLabel(feature.jsd_distance)
      : null;

  return (
    // lg:hidden — 데스크톱(>=lg)에서는 테이블 오른쪽 FeatureDetailPanel이 동일 정보를
    // 더 풍부하게 표시하므로 인라인행을 숨겨 중복을 방지한다. 좁은 화면에서만 노출.
    <tr data-testid="feature-detail-inline-row" className="lg:hidden">
      {/* 표 전체를 가로질러야 하므로 FeaturesTable 의 컬럼 수를 그대로 쓴다.
          하드코딩(과거 7)은 컬럼 추가·표시 플래그 변경마다 어긋난다. */}
      <td colSpan={COLUMN_COUNT} className="p-0">
        <div className="bg-blue-50 border-t border-b border-blue-200 px-4 py-3">
          <div className="flex gap-4 flex-wrap">
            {/* Identity */}
            <div className="flex-1 min-w-48">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-xs text-blue-600">
                  {feature.feature_id}
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {feature.predicted_substance}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                RT {formatRt(feature.rt)} min / m/z {feature.top_mz}
                {SHOW_MATCH_PERCENTAGE && ` / 매칭 ${feature.matching_score}%`}
                {/* T1: 같은 스캔 후보 중 몇 순위인지 — 표의 관찰 헤더와 짝 */}
                {feature.candidate_rank != null &&
                  feature.observation_size != null &&
                  feature.observation_size > 1 &&
                  ` / 관찰 내 ${feature.candidate_rank}순위 (같은 스캔에 기록된 후보 ${feature.observation_size}개)`}
              </p>
              {SHOW_AI_NOTE && feature.ai_note && (
                <p className="text-xs text-gray-600 mt-1 italic">
                  {feature.ai_note}
                </p>
              )}
            </div>

            {/* Confidence */}
            {feature.jsd_distance != null && (
              <div className="min-w-32">
                <p className="text-xs font-semibold text-gray-600 mb-1">
                  JSD 거리
                </p>
                <span
                  className={`text-xs font-bold font-mono ${jsdInfo ? jsdInfo.cls : "text-gray-800"}`}
                >
                  {feature.jsd_distance.toFixed(3)}
                  {jsdInfo && ` — ${jsdInfo.label}`}
                </span>
              </div>
            )}

            {/* Properties */}
            {(feature.predicted_vp != null ||
              feature.predicted_solubility != null) && (
              <div className="min-w-32">
                <p className="text-xs font-semibold text-gray-600 mb-1">
                  KROVEX 물성
                </p>
                <div className="space-y-0.5">
                  {/* 2026-08-28 (P2-4): 데스크톱 상세 패널과 같은 헬퍼를 쓴다.
                      그 전에는 같은 보정값(+2.0)을 여기서만 '보정' 표기 없이
                      보여줘, 좁은 화면 사용자에게는 모델 원값처럼 읽혔다. */}
                  {feature.predicted_vp != null && (
                    <p
                      className="text-xs font-mono text-purple-800"
                      title={vpTooltip(feature.predicted_vp)}
                    >
                      VP {vpValueText(feature.predicted_vp)}
                    </p>
                  )}
                  {feature.predicted_solubility != null && (
                    <p className="text-xs font-mono text-purple-800">
                      Sol {feature.predicted_solubility.toFixed(2)} log S
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Molecular structure */}
            {feature.smiles && (
              <div>
                <MolecularStructure
                  smiles={feature.smiles}
                  width={120}
                  height={80}
                />
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}
